import { useEffect, useRef, useState } from "react";
import { api, type DriverLocation } from "../api";
import BackHeader from "../components/BackHeader";

const LEAFLET_VERSION = "1.9.4";
const REFRESH_MS = 15_000;
const STALE_MS = 30 * 60_000;
const OSLO: [number, number] = [59.9139, 10.7522];

// Leaflet is loaded at runtime from a CDN (kept out of the bundle; only the /map page needs it).
type Leaflet = {
  map: (el: HTMLElement, opts?: object) => LMap;
  tileLayer: (url: string, opts: object) => { addTo: (m: LMap) => void };
  marker: (latlng: [number, number], opts?: object) => LMarker;
  latLngBounds: (points: [number, number][]) => object;
  divIcon: (opts: object) => object;
};
type LMap = { setView: (c: [number, number], z: number) => LMap; fitBounds: (b: object, o?: object) => void; remove: () => void };
type LMarker = { addTo: (m: LMap) => LMarker; setLatLng: (l: [number, number]) => void; bindPopup: (s: string) => LMarker; setPopupContent: (s: string) => void; setOpacity: (o: number) => void; remove: () => void };

let leafletLoading: Promise<Leaflet> | null = null;
function loadLeaflet(): Promise<Leaflet> {
  const w = window as unknown as { L?: Leaflet };
  if (w.L) return Promise.resolve(w.L);
  if (leafletLoading) return leafletLoading;
  leafletLoading = new Promise((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
    js.onload = () => (w.L ? resolve(w.L) : reject(new Error("Leaflet failed to load")));
    js.onerror = () => reject(new Error("Could not load the map library"));
    document.head.appendChild(js);
  });
  return leafletLoading;
}

function ago(iso: string, now: number) {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${m % 60} min ago`;
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const COLORS = ["#d8647a", "#3b82f6", "#16a34a", "#f59e0b", "#8b5cf6"];

/** Hidden page at /map: last known position of every driver who shares location. */
export default function MapScreen({ onBack }: { onBack: () => void }) {
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LMap | null>(null);
  const markers = useRef(new Map<number, LMarker>());
  const [rows, setRows] = useState<DriverLocation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapEl.current) return;
        const map = L.map(mapEl.current, { zoomControl: true }).setView(OSLO, 10);
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
        }).addTo(map);
        mapRef.current = map;

        let fitted = false;
        const refresh = async () => {
          try {
            const list = await api.locations();
            if (cancelled) return;
            setRows(list);
            setError(null);
            const t = Date.now();
            setNow(t);
            const seen = new Set<number>();
            list.forEach((loc, i) => {
              seen.add(loc.driverId);
              const stale = t - new Date(loc.updatedAt).getTime() > STALE_MS;
              const popup = `<b>${loc.name}</b><br>${ago(loc.updatedAt, t)}${loc.accuracy ? ` · ±${Math.round(loc.accuracy)} m` : ""}`;
              let marker = markers.current.get(loc.driverId);
              if (!marker) {
                const icon = L.divIcon({
                  className: "",
                  html: `<div style="background:${COLORS[i % COLORS.length]};color:#fff;font:700 12px 'Avenir Next','Nunito Sans',sans-serif;padding:4px 10px;border-radius:999px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3);transform:translate(-50%,-50%)">${loc.name}</div>`,
                  iconSize: [0, 0],
                });
                marker = L.marker([loc.lat, loc.lng], { icon }).addTo(map).bindPopup(popup);
                markers.current.set(loc.driverId, marker);
              } else {
                marker.setLatLng([loc.lat, loc.lng]);
                marker.setPopupContent(popup);
              }
              marker.setOpacity(stale ? 0.45 : 1);
            });
            for (const [id, m] of markers.current) {
              if (!seen.has(id)) {
                m.remove();
                markers.current.delete(id);
              }
            }
            if (!fitted && list.length) {
              fitted = true;
              map.fitBounds(L.latLngBounds(list.map((l) => [l.lat, l.lng] as [number, number])), { padding: [40, 40], maxZoom: 14 });
            }
          } catch {
            if (!cancelled) setError("Couldn't load driver locations.");
          }
        };
        refresh();
        timer = setInterval(refresh, REFRESH_MS);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Map failed to load"));
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      markers.current.clear();
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col gap-4 px-4 pb-6 pt-4">
      <BackHeader title="Drivers map" onBack={onBack} />
      <div ref={mapEl} className="h-[55dvh] w-full overflow-hidden rounded-[18px] bg-white border border-line-soft shadow-card" />
      {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
      {rows?.length === 0 && (
        <p className="text-center text-sm font-semibold text-ink/50">
          No driver is sharing location yet. Drivers turn it on with the pin button next to their name.
        </p>
      )}
      {!!rows?.length && (
        <ul className="flex flex-col gap-2">
          {rows.map((loc, i) => {
            const stale = now - new Date(loc.updatedAt).getTime() > STALE_MS;
            return (
              <li key={loc.driverId} className={`flex items-center justify-between rounded-[14px] bg-white px-4 py-3 border border-line-soft shadow-card ${stale ? "opacity-60" : ""}`}>
                <span className="flex items-center gap-2 font-semibold">
                  <span className="inline-block size-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  {loc.name}
                </span>
                <span className="text-xs font-semibold text-ink/50">
                  {ago(loc.updatedAt, now)}
                  {loc.accuracy ? ` · ±${Math.round(loc.accuracy)} m` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-center text-xs font-semibold text-ink/40">
        Positions update only while a driver has the app open with sharing on. Refreshes every 15 s.
      </p>
    </div>
  );
}
